import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { SupportedIntegrationPlatform, User } from '@core/generated/gql-types';
import { actionPrimaryButtonClassName } from '@core/theme/actionTheme.util';
import { useIntegrationStore } from '@core/integration/useIntegrationStore';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import { formatTime, formatDate, getCustomAttribute } from '@/utils/utils';
import {
  vevoConditionCodes,
  isCitizenOrPermanentResident,
  residencyStatusLabel,
  parseVevoVisaExpiry,
} from '@/modules/workers/utils/workEligibility.utils';
import { Spinner } from '@/components/shared/Spinner';
import WorkEligibilityModal from './WorkEligibilityModal';

interface VevoWorkEntitlementProps {
  worker: User;
}

const VevoWorkEntitlement: React.FC<VevoWorkEntitlementProps> = ({
  worker,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isVevoLoading, setIsVevoLoading] = useState(true);
  const { fetchDetailedWorkerById } = useWorkerStore();
  const vevoCheck = worker.vevoCheck;
  const residencyStatusRaw =
    vevoCheck?.residencyStatus ||
    getCustomAttribute(worker, 'residencyStatusWithMandatoryValidVisa') ||
    getCustomAttribute(worker, 'residencyStatus') ||
    getCustomAttribute(worker, 'residencyStatusWithMandatoryValidVisa') ||
    '';
  const residencyStatus = residencyStatusRaw.trim() || undefined;
  const showCitizenOrPrEntitlement =
    isCitizenOrPermanentResident(residencyStatus);

  const ensureIntegrationConnections = useIntegrationStore(
    (s) => s.ensureIntegrationConnections,
  );
  const integrationStatus = useIntegrationStore((s) => s.integrationStatus);
  const loadedIntegrationTenantId = useIntegrationStore(
    (s) => s.loadedTenantId,
  );
  const vevoIntegration = useIntegrationStore((s) =>
    s.integrationConnections.find(
      (c) =>
        s.integrationPlatformByIntegrationId[c.integrationId] ===
        SupportedIntegrationPlatform.Vevo,
    ),
  );

  const isVevoIntegrationReady =
    !!worker.tenantId &&
    loadedIntegrationTenantId === worker.tenantId &&
    integrationStatus === 'ready' &&
    !!vevoIntegration &&
    vevoIntegration.integrationEnabled !== false;

  useEffect(() => {
    if (worker.tenantId) {
      void ensureIntegrationConnections(worker.tenantId);
    }
  }, [worker.tenantId, ensureIntegrationConnections]);

  useEffect(() => {
    if (!isVevoIntegrationReady) {
      return;
    }

    let isCancelled = false;

    const fetchVevoCheck = async () => {
      try {
        setIsVevoLoading(true);
        await fetchDetailedWorkerById(worker.id);
      } catch (error) {
        console.error(`Error fetching VEVO check: ${error}`);
      } finally {
        if (!isCancelled) {
          setIsVevoLoading(false);
        }
      }
    };

    void fetchVevoCheck();

    return () => {
      isCancelled = true;
    };
  }, [isVevoIntegrationReady, worker.id, fetchDetailedWorkerById]);

  const missingVevoData = useMemo(() => {
    const missingFields = [];

    const passportNumber = getCustomAttribute(worker, 'passportNumber');
    if (!passportNumber) {
      missingFields.push('Passport number');
    }

    const passportCountry = getCustomAttribute(worker, 'passportIssueCountry');
    if (!passportCountry) {
      missingFields.push('Passport country');
    }

    const dob = worker.dob;
    if (!dob) {
      missingFields.push('Date of birth');
    }

    const firstName = worker.firstName?.trim();
    if (!firstName) {
      missingFields.push('First name');
    }

    const lastName = worker.lastName?.trim();
    if (!lastName) {
      missingFields.push('Last name');
    }

    return missingFields;
  }, [worker]);

  const isVevoCheckValid = useMemo(() => {
    if (!vevoCheck?.vevoCheckWorkConditionCode?.trim()) {
      return false;
    }

    if (vevoCheck.expiresAt) {
      return dayjs(parseInt(vevoCheck.expiresAt)).isAfter(dayjs());
    }

    if (vevoCheck.vevoCheckVisaExpiry) {
      const visaExpiry = parseVevoVisaExpiry(vevoCheck.vevoCheckVisaExpiry);
      return visaExpiry ? visaExpiry.isAfter(dayjs()) : false;
    }

    // Some VEVO checks can be valid without a visa expiry value.
    return true;
  }, [
    vevoCheck?.vevoCheckWorkConditionCode,
    vevoCheck?.expiresAt,
    vevoCheck?.vevoCheckVisaExpiry,
  ]);

  const hasVevoCheckData = useMemo(() => {
    if (!vevoCheck) {
      return false;
    }

    return Boolean(
      vevoCheck.vevoCheckWorkConditionCode?.trim() ||
      vevoCheck.vevoCheckVisaSubClassCode?.trim() ||
      vevoCheck.vevoCheckWorkEntitlementStatus?.trim() ||
      vevoCheck.createdAt,
    );
  }, [vevoCheck]);

  const isVerified = showCitizenOrPrEntitlement || isVevoCheckValid;

  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  if (!isVevoIntegrationReady) {
    return null;
  }

  const getWorkConditionTitle = (
    workConditionCode: string | null | undefined,
  ) => {
    if (!workConditionCode) {
      return '—';
    }

    if (workConditionCode in vevoConditionCodes) {
      return vevoConditionCodes[
        workConditionCode as keyof typeof vevoConditionCodes
      ]?.title;
    }

    return workConditionCode;
  };

  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <div
        className="mb-4 flex cursor-pointer items-center gap-2 select-none"
        onClick={handleToggleCollapse}
      >
        <span className="text-gray-400">
          {isCollapsed ? (
            <ChevronRightIcon className="h-5 w-5" />
          ) : (
            <ChevronDownIcon className="h-5 w-5" />
          )}
        </span>
        <h2 className="text-xl font-semibold">VEVO Work Entitlement</h2>
        {!isVevoLoading && (
          <span className="flex items-center gap-1.5 text-sm text-gray-600">
            <span
              className={`h-2 w-2 rounded-full ${isVerified ? 'bg-green-500' : 'bg-amber-500'}`}
            />
            {isVerified ? 'Verified' : 'Not Verified'}
          </span>
        )}
      </div>

      {!isCollapsed && isVevoLoading && (
        <div className="flex items-center justify-center py-8">
          <Spinner />
        </div>
      )}

      {!isCollapsed && !isVevoLoading && (
        <>
          {showCitizenOrPrEntitlement && residencyStatus ? (
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <dt className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                Work entitlement
              </dt>
              <dd className="mt-1 text-lg font-semibold text-gray-900">
                {residencyStatusLabel(residencyStatus)}
              </dd>
              <p className="mt-3 text-sm text-gray-600">
                Work rights are verified from residency status.
              </p>
            </div>
          ) : (
            <>
              {missingVevoData.length > 0 && !isVevoCheckValid && (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                  <p className="text-sm font-medium text-amber-800">
                    Complete these fields to run a VEVO check:
                  </p>
                  <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-amber-700">
                    {missingVevoData.map((field) => (
                      <li key={field}>{field}</li>
                    ))}
                  </ul>
                </div>
              )}
              {hasVevoCheckData && vevoCheck ? (
                <>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="min-w-0 rounded-lg border border-gray-200 bg-white p-4">
                      <dt className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                        VISA SUBCLASS
                      </dt>
                      <dd className="mt-1 text-lg font-semibold text-gray-900">
                        {vevoCheck.vevoCheckVisaSubClassCode ?? '—'}
                      </dd>
                      <dd className="mt-0.5 text-sm text-gray-600"></dd>
                    </div>
                    <div className="min-w-0 rounded-lg border border-gray-200 bg-white p-4">
                      <dt className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                        WORK CONDITION
                      </dt>
                      <dd className="mt-1 text-lg font-semibold text-gray-900">
                        {getWorkConditionTitle(
                          vevoCheck.vevoCheckWorkConditionCode,
                        )}
                      </dd>
                    </div>
                    <div className="min-w-0 rounded-lg border border-gray-200 bg-white p-4">
                      <dt className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                        WORK ENTITLEMENT STATUS
                      </dt>
                      <dd className="mt-1 text-lg font-semibold text-gray-900">
                        {vevoCheck.vevoCheckWorkEntitlementStatus?.trim()
                          ? vevoCheck.vevoCheckWorkEntitlementStatus
                          : '—'}
                      </dd>
                      <dd className="mt-0.5 text-sm text-gray-600">
                        {vevoCheck.vevoCheckWorkEntitlementStatus !==
                        'UNLIMITED'
                          ? `${vevoCheck.maxHourLimit} hrs per week cap`
                          : '—'}
                      </dd>
                    </div>
                    <div className="min-w-0 rounded-lg border border-gray-200 bg-white p-4">
                      <dt className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                        VISA EXPIRY
                      </dt>
                      <dd className="mt-1 text-lg font-semibold text-gray-900">
                        {parseVevoVisaExpiry(
                          vevoCheck.vevoCheckVisaExpiry,
                        )?.format('D MMMM YYYY') ?? '—'}
                      </dd>
                    </div>
                  </div>
                  {vevoCheck.createdAt && (
                    <div className="mt-4 rounded-lg bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-800">
                      Last checked {formatDate(vevoCheck.createdAt)} at{' '}
                      {formatTime(vevoCheck.createdAt)}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-500">
                  {missingVevoData.length > 0
                    ? 'Complete the missing fields above to see work entitlement.'
                    : 'No VEVO check data available. Run a check to see work entitlement.'}
                </p>
              )}
            </>
          )}
          <div className="mt-4 flex justify-start">
            <button
              type="button"
              onClick={() => setIsUpdateModalOpen(true)}
              className={`rounded-md px-4 py-2 text-sm font-medium ${actionPrimaryButtonClassName}`}
            >
              Update
            </button>
          </div>
        </>
      )}

      <WorkEligibilityModal
        worker={worker}
        isOpen={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
      />
    </div>
  );
};

export default VevoWorkEntitlement;
